import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus } from 'lucide-react';
import {
  Button,
  Input,
  Textarea,
  Label,
  Alert,
  AlertDescription,
  useAuth,
  kmsService,
} from '@aaas/shared';
import { SpaceFormData } from '../types';

const CreateSpacePage = () => {
  const navigate = useNavigate();
  const { user, currentWorkspace, idToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<SpaceFormData>({
    name: '',
    description: '',
  });

  const handleInputChange = (field: keyof SpaceFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !currentWorkspace || !idToken) {
      setError('User or workspace not found');
      return;
    }

    // Basic validation
    if (!formData.name.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const newSpace = await kmsService.createSpace(idToken, {
        name: formData.name.trim(),
        description: formData.description?.trim() || undefined,
      });

      // Redirect to the new space detail page
      navigate(`/kms/spaces/${newSpace.data.id}`);
    } catch (err: unknown) {
      // Handle space creation errors gracefully

      // Handle specific error messages
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { status?: number; data?: { message?: string } } };
        if (axiosError.response?.status === 400) {
          setError(axiosError.response.data?.message || 'Space with this code already exists');
        } else {
          setError('Failed to create space');
        }
      } else {
        setError(err instanceof Error ? err.message : 'Failed to create space');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/kms/spaces')} className="p-2">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h1 className="text-3xl font-bold">Create New Space</h1>
      </div>

      {/* Form */}
      <div className="max-w-2xl">
        <div className="bg-white rounded-lg border p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Alert */}
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Space Name */}
            <div className="space-y-2">
              <Label htmlFor="name">
                Space Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Enter space name..."
                disabled={loading}
                maxLength={255}
                required
              />
              <p className="text-sm text-gray-500">A descriptive name for your business space</p>
            </div>

            {/* Space Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Optional description of the space..."
                disabled={loading}
                maxLength={1000}
                rows={5}
              />
              <p className="text-sm text-gray-500">
                Optional description to help others understand this space (max 1000 characters)
              </p>
            </div>

            {/* Submit Buttons */}
            <div className="flex items-center gap-4 pt-4">
              <Button type="submit" disabled={loading} className="bg-green-600 hover:bg-green-700">
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Space
                  </>
                )}
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/kms/spaces')}
                disabled={loading}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateSpacePage;
